import { Form, Modal, Select, Spin, Typography, Space, Button } from 'antd';
import { useEffect, useState } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import {
  getCompanies,
  downloadCompanyLogs,
  downloadAllLogs,
} from '../../utils/httpRequest';

export interface DownloadLogModalProps {
  open: boolean;
  onClose: () => void;
}

const DownloadLogModal = ({ open, onClose }: DownloadLogModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<
    { companyID: string; name: string }[]
  >([]);
  const [fetchingCompanies, setFetchingCompanies] = useState(false);

  const fetchCompanies = async () => {
    try {
      setFetchingCompanies(true);
      const data = await getCompanies();
      setCompanies(data || []);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    } finally {
      setFetchingCompanies(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCompanies();
    } else {
      form.resetFields();
    }
  }, [open]);

  const handleDownload = async () => {
    try {
      setLoading(true);
      const { companyName } = await form.validateFields();

      let blobData;
      let fileName;

      if (companyName === 'ALL') {
        const result = await downloadAllLogs();
        blobData = result.blob;
        fileName = result.fileName;
      } else {
        const result = await downloadCompanyLogs(companyName);
        blobData = result.blob;
        fileName = result.fileName;
      }
      const blob = new Blob([blobData], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const companyOptions = [
    { label: 'All Companies', value: 'ALL' },
    ...companies.map((c) => ({
      label: c.name,
      value: c.name,
    })),
  ];

  return (
    <Modal
      open={open}
      title="Download Company Logs"
      onCancel={onClose}
      onClose={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={loading}
          >
            Download
          </Button>
        </Space>
      }
    >
      <Spin spinning={fetchingCompanies}>
        <Typography.Paragraph type="secondary">
          Select a company to download its logs, or choose &quot;All
          Companies&quot; to download logs from all companies.
        </Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item
            label="Company"
            name="companyName"
            rules={[{ required: true, message: 'Please select a company' }]}
          >
            <Select
              placeholder="Select a company"
              showSearch
              optionFilterProp="label"
              options={companyOptions}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default DownloadLogModal;
